/* eslint-disable no-unused-vars */
import clsx from "clsx";
import { SwaptionDirection } from "@/lib/interfaces and Enums/ENUMOptionTradeMessage";
import { TradeDirection } from "@/lib/interfaces and Enums/ENUMTradeDirection";

// LQC-2254 / LQC-2494 - the direction row for any ticket. Starts empty so the user has
// to choose, and the control itself turns green or red once they have. Nothing in the
// app coloured a direction before this: the only direction colouring that exists is a
// whole-row AG Grid background driven by user-picked colours, and it is off by default.
//
// The options are passed in because the vocabulary is per instrument - a bond buys and
// sells, a future takes Buy Future / Sell Future.

interface IDirectionSelect<T extends string> {
  value: T;
  onChange: (value: T) => void;
  testId: string;
  // Plain strings: some are screen-only, like the swap's Buy / Sell Inflation.
  directions?: string[];
  // Shorter display text for tight cells (Q22). The value and tooltip stay full.
  labels?: Record<string, string>;
  isDisabled?: boolean;
}

// The bond pair. The enum other ten members are swap, repo and future vocabulary.
export const TICKET_DIRECTIONS: TradeDirection[] = [
  TradeDirection.Buy,
  TradeDirection.Sell,
];

// Buys are green whatever the instrument calls them.
const BUY_DIRECTIONS: string[] = [
  TradeDirection.Buy,
  TradeDirection.BuyFuture,
  SwaptionDirection.BuyPayer,
  SwaptionDirection.BuyReceiver,
];
const SELL_DIRECTIONS: string[] = [
  TradeDirection.Sell,
  TradeDirection.SellFuture,
  SwaptionDirection.SellPayer,
  SwaptionDirection.SellReceiver,
];

export function returnDirectionColourClass(value: string): string {
  if (BUY_DIRECTIONS.includes(value)) {
    return "border-green-600 bg-green-600/15 font-semibold text-green-700 dark:text-green-400";
  }
  if (SELL_DIRECTIONS.includes(value)) {
    return "border-red-600 bg-red-600/15 font-semibold text-red-700 dark:text-red-400";
  }
  return "border-gray-300 dark:border-gray-600 dark:bg-gray-800";
}

// LQC-3027 - which side a direction is on, for styling that should not key off class names.
export function returnDirectionSide(value: string): "buy" | "sell" | undefined {
  if (BUY_DIRECTIONS.includes(value)) return "buy";
  if (SELL_DIRECTIONS.includes(value)) return "sell";
  return undefined;
}

// Options would otherwise inherit the control's colour, so each sets its own on a plain background.
export function returnDirectionOptionClass(value: string): string {
  const base = "bg-white font-normal dark:bg-gray-800";
  if (BUY_DIRECTIONS.includes(value)) {
    return clsx(base, "font-semibold text-green-700 dark:text-green-400");
  }
  if (SELL_DIRECTIONS.includes(value)) {
    return clsx(base, "font-semibold text-red-700 dark:text-red-400");
  }
  return clsx(base, "text-gray-900 dark:text-gray-200");
}

const DirectionSelect = <T extends string>({
  value,
  onChange,
  testId,
  directions = TICKET_DIRECTIONS,
  labels,
  isDisabled = false,
}: IDirectionSelect<T>) => {
  return (
    <select
      data-testid={testId}
      id={testId}
      value={value}
      // A narrow grid column cuts the closed text ("Buy Inflati"); the tooltip has it whole.
      title={value || undefined}
      data-side={returnDirectionSide(value)}
      disabled={isDisabled}
      onChange={(e) => onChange(e.target.value as T)}
      className={clsx(
        "h-[28px] w-full rounded-md border px-1 text-xs outline-none dark:text-gray-200",
        returnDirectionColourClass(value),
        isDisabled && "cursor-not-allowed opacity-50",
      )}
    >
      <option value="" className={returnDirectionOptionClass("")}>
        Select ...
      </option>
      {directions.map((direction) => (
        <option
          key={direction}
          value={direction}
          className={returnDirectionOptionClass(direction)}
        >
          {labels?.[direction] ?? direction}
        </option>
      ))}
    </select>
  );
};

export default DirectionSelect;
